/**
 * pages/admin/routes.jsx
 * ----------------------
 * Route-wrapper pages for the admin dashboard. Each export is a tiny
 * component that:
 *   • Wraps the unified `<DashboardLayout>` shell (sidebar + gutter).
 *   • Mounts the existing `<AdminPage>` with a `defaultTab` + `embedded`
 *     prop so its internal h1, action buttons, and horizontal TabsList
 *     are hidden — the sidebar takes over as primary navigation.
 *
 * ZERO business logic touched. ZERO API changes. Just composition.
 *
 * v2.71.32 — Initial route-based dashboard migration.
 */

import React, { lazy, Suspense } from "react";
import { DashboardLayout } from "../../features/dashboard";
import { DashboardSkeleton } from "../../components/Skeletons";

// Lazy-load the legacy AdminPage so each route's first paint is fast.
const AdminPage = lazy(() => import("../AdminPage"));
// v2.71.104 — Standalone Backup Center page (does not piggyback AdminPage).
const AdminBackupCenterTab = lazy(() => import("../AdminBackupCenterTab"));

/** Factory that produces a route page wrapping AdminPage at the given tab. */
function withAdmin(tabValue, label, subtitle) {
  const Wrapped = function AdminRoutePage() {
    return (
      <DashboardLayout title={label} subtitle={subtitle}>
        <Suspense fallback={<DashboardSkeleton stats={4} rows={6} />}>
          <AdminPage defaultTab={tabValue} embedded />
        </Suspense>
      </DashboardLayout>
    );
  };
  Wrapped.displayName = `AdminRoute(${tabValue})`;
  return Wrapped;
}

export const AdminListings        = withAdmin("listings",      "Listings",        "Manage all properties");
export const AdminApprovals       = withAdmin("approvals",     "Approvals",       "Pending property reviews");
export const AdminUsers           = withAdmin("users",         "Users",           "Client accounts");
export const AdminDealers         = withAdmin("dealers",       "Dealers / KYC",   "Dealer & builder roster");
export const AdminAddClient       = withAdmin("add-client",    "Add Client",      "Onboard a new client");
export const AdminCRM             = withAdmin("crm",           "Leads / CRM",     "Inquiries, callbacks, pipeline");
export const AdminPlans           = withAdmin("plans",         "Plans",           "Subscription plans");
export const AdminContent         = withAdmin("content",       "Content / CMS",   "Site content and copy");
export const AdminPages           = withAdmin("pages",         "Pages",           "Privacy, Terms, custom pages");
export const AdminTransactions    = withAdmin("transactions",  "Transactions",    "Payments and refunds");
export const AdminSettings        = withAdmin("settings",      "Settings",        "Platform configuration");
export const AdminProjects        = withAdmin("projects",      "Projects",        "Builder project inventory");
export const AdminTeam            = withAdmin("team",          "Team",            "Internal team members");
export const AdminFilters         = withAdmin("filters",       "Filters & Areas", "Schema and dynamic filters");
export const AdminLocations       = withAdmin("locations",     "Locations",       "Areas and sub-areas");
export const AdminTestimonials    = withAdmin("testimonials",  "Testimonials",    "User testimonials");
export const AdminShortlists      = withAdmin("shortlists",    "Shortlists",      "Curated property bundles");
export const AdminNotifications   = withAdmin("notifications", "Notifications",   "Platform notifications");

/**
 * v2.71.104 — Backup Center.
 * Standalone page (not an AdminPage tab) — uses its own dedicated component
 * so the route stays focused and lightweight.
 */
export const AdminBackupCenter = function AdminBackupCenterRoute() {
  return (
    <DashboardLayout title="Backup Center" subtitle="Exports, snapshots, schedule and restore">
      <Suspense fallback={<DashboardSkeleton stats={2} rows={4} />}>
        <AdminBackupCenterTab />
      </Suspense>
    </DashboardLayout>
  );
};

/** Map: legacy `?tab=` value → new route path. Used by the entry redirect. */
export const ADMIN_TAB_TO_ROUTE = {
  overview:       "/admin/overview",
  listings:       "/admin/listings",
  approvals:      "/admin/approvals",
  pending:        "/admin/approvals",    // legacy alias (internal tab name)
  users:          "/admin/users",
  dealers:        "/admin/dealers",
  "add-client":   "/admin/add-client",
  "create-user":  "/admin/add-client",   // legacy alias (internal tab name)
  crm:            "/admin/crm",
  leads:          "/admin/crm",       // legacy alias
  enquiries:      "/admin/crm",       // legacy alias
  plans:          "/admin/plans",
  content:        "/admin/content",
  pages:          "/admin/pages",
  transactions:   "/admin/transactions",
  settings:       "/admin/settings",
  projects:       "/admin/projects",
  team:           "/admin/team",
  filters:        "/admin/filters",
  locations:      "/admin/locations",
  testimonials:   "/admin/testimonials",
  shortlists:     "/admin/shortlists",
  notifications:  "/admin/notifications",
};
